/**
 * GPS Alerts Page
 * View and acknowledge GPS alert events
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  useWindowDimensions,
  Platform,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Surface, useTheme, Appbar } from 'react-native-paper';
import { useRouter } from 'expo-router';
import gpsTrackingAPI from '../../../src/api/services/gpsTracking';
import { Sidebar, SIDEBAR_WIDTH } from '../../../src/components/Sidebar';
import { useUser } from '../../../src/hooks/useUser';
import { useProfile } from '../../../src/hooks/useProfile';
import type { AlertEvent } from '../../../src/types/gps';
import Logo from '../../../src/components/Logo';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

export default function AlertsPage() {
  const theme = useTheme();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isLargeScreen = width > 1024;
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarVisible, setDesktopSidebarVisible] = useState(isLargeScreen);
  
  const [alerts, setAlerts] = useState<AlertEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [typeFilter, setTypeFilter] = useState<'all' | 'speed' | 'geofence' | 'offline' | 'other'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'type'>('newest');
  
  const { handleLogout, userData } = useUser();
  const { selectedProfile, effectiveProfile, updateProfile, hasOwner, hasDriver } = useProfile(userData?.groups || []);
  
  useEffect(() => {
    if (isLargeScreen) {
      setDesktopSidebarVisible(true);
      setSidebarOpen(false);
    } else {
      setDesktopSidebarVisible(false);
    }
  }, [isLargeScreen, width]);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await gpsTrackingAPI.alerts.listEvents();
      setAlerts(data);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAlerts();
    setRefreshing(false);
  };

  const getAlertIcon = (eventType: string) => {
    const type = eventType.toLowerCase();
    if (type.includes('geofence')) return 'location-outline';
    if (type.includes('speed')) return 'speedometer-outline';
    if (type.includes('offline') || type.includes('online')) return 'wifi-outline';
    if (type.includes('ignition')) return 'key-outline';
    if (type.includes('battery')) return 'battery-half-outline';
    if (type.includes('sos')) return 'warning-outline';
    return 'notifications-outline';
  };

  const getFriendlyEventType = (eventType: string) => {
    const labels: Record<string, string> = {
      speed_exceeded: 'Excesso de Velocidade',
      overspeed: 'Excesso de Velocidade',
      geofence_enter: 'Entrada em Geocerca',
      geofence_exit: 'Saída de Geocerca',
      geofence: 'Geocerca',
      device_offline: 'Dispositivo Offline',
      device_online: 'Dispositivo Online',
      ignition_on: 'Ignição Ligada',
      ignition_off: 'Ignição Desligada',
      ignition: 'Ignição',
      low_battery: 'Bateria Fraca',
      sos: 'SOS',
      harsh_braking: 'Travagem Brusca',
      harsh_acceleration: 'Aceleração Brusca',
      harsh_cornering: 'Curva Brusca',
      idle_excessive: 'Marcha Lenta Excessiva',
      maintenance_due: 'Manutenção Pendente',
    };

    const normalized = eventType.toLowerCase();
    return labels[normalized] || eventType.replace(/_/g, ' ');
  };

  const getFriendlyFieldLabel = (key: string) => {
    const labels: Record<string, string> = {
      speed: 'Velocidade',
      speedLimit: 'Limite de Velocidade',
      ignition: 'Ignição',
      geofence: 'Geocerca',
      batteryLevel: 'Nível da Bateria',
      alarm: 'Alarme',
      motion: 'Em movimento',
      address: 'Morada',
      latitude: 'Latitude',
      longitude: 'Longitude',
      eventTime: 'Hora do Evento',
      serverTime: 'Hora do Servidor',
      fixTime: 'Hora do GPS',
    };

    return labels[key] || key.replace(/_/g, ' ');
  };

  const getAlertColor = (eventType: string, status: string) => {
    if (status === 'acknowledged') return '#8E8E93';
    
    const type = eventType.toLowerCase();
    if (type.includes('sos') || type.includes('speed')) return '#FF3B30';
    if (type.includes('offline')) return '#FF9500';
    if (type.includes('online')) return '#34C759';
    return '#007AFF';
  };

  const getSpeedKmh = (alert: AlertEvent) => {
    const speedKnots = alert.event_data?.position?.speed;
    if (typeof speedKnots === 'number' && !Number.isNaN(speedKnots)) {
      return speedKnots * 1.852;
    }
    const match = alert.message.match(/([0-9]+(?:[.,][0-9]+)?)\s*km\/h/i);
    if (!match) return null;
    const parsed = Number(match[1].replace(',', '.'));
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getPrimaryName = (alert: AlertEvent) => {
    const eventData = alert.event_data || {};
    const vehicleRegistration =
      eventData?.vehicle_registration ||
      eventData?.vehicle?.registration_number ||
      eventData?.device?.vehicle_registration;

    const hasVehicleAssociated = Boolean(
      eventData?.vehicle_id ||
      eventData?.vehicle?.id ||
      eventData?.vehicle ||
      eventData?.vehicle_registration ||
      eventData?.device?.vehicle_registration
    );

    if (hasVehicleAssociated) {
      return vehicleRegistration || 'Veículo sem matrícula';
    }

    return alert.device_label;
  };

  const buildRouteLinkParams = (alert: AlertEvent) => {
    const eventTime = alert.created_at ? new Date(alert.created_at) : null;
    if (!eventTime || Number.isNaN(eventTime.getTime()) || !alert.device) return null;

    const windowMinutes = 30;
    const from = new Date(eventTime.getTime() - windowMinutes * 60 * 1000);
    const to = new Date(eventTime.getTime() + windowMinutes * 60 * 1000);

    return {
      pathname: '/dashboard/gps/reports',
      params: {
        reportType: 'routes',
        device_id: String(alert.device),
        from: from.toISOString(),
        to: to.toISOString(),
      },
    };
  };

  const displayedAlerts = useMemo(() => {
    const byType = alerts.filter((alert) => {
      const eventType = alert.event_type.toLowerCase();
      if (typeFilter === 'all') return true;
      if (typeFilter === 'speed') return eventType.includes('speed') || eventType.includes('overspeed');
      if (typeFilter === 'geofence') return eventType.includes('geofence');
      if (typeFilter === 'offline') return eventType.includes('offline') || eventType.includes('online');
      return !eventType.includes('speed') && !eventType.includes('overspeed') && !eventType.includes('geofence') && !eventType.includes('offline') && !eventType.includes('online');
    });

    const sorted = [...byType];
    sorted.sort((a, b) => {
      if (sortBy === 'oldest') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (sortBy === 'type') return getFriendlyEventType(a.event_type).localeCompare(getFriendlyEventType(b.event_type));
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return sorted;
  }, [alerts, typeFilter, sortBy]);

  const renderHeader = () => {
    if (isLargeScreen) return null;
    
    return (
      <Appbar.Header style={{ 
        height: SCREEN_HEIGHT * 0.1, 
        backgroundColor: theme.colors.surface,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1
      }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} color={theme.colors.onSurface} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo 
            size={Platform.OS === 'web' ? 'medium' : 'mobile'} 
            style={{ maxWidth: Platform.OS === 'web' ? 200 : 250 }} 
          />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  return (
    <View style={styles.outerContainer}>
      {/* Desktop Sidebar */}
      {desktopSidebarVisible && (
        <Sidebar
          key={`desktop-sidebar-${width}`}
          isOpen={true}
          onClose={() => {}}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner && hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}

      {/* Main Content */}
      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(desktopSidebarVisible ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        {renderHeader()}
        
        <View style={[styles.container, !isLargeScreen && { marginTop: SCREEN_HEIGHT * 0.1 }]}>
          {/* Loading overlay */}
          {loading && (
            <View style={styles.loadingOverlay}>
              <ActivityIndicator size="large" color="#007AFF" />
            </View>
          )}

          {!loading && (
            <>
      {/* Header with quick switch */}
      <View style={styles.pageHeader}>
        <View style={styles.headerTopRow}>
          <Text style={styles.pageTitle}>Alertas</Text>
        </View>

        <View style={styles.switcher}>
          <TouchableOpacity
            style={[styles.switchButton, styles.switchButtonActive]}
            onPress={() => {}}
          >
            <Text style={[styles.switchButtonText, styles.switchButtonTextActive]}>Ver alertas</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.switchButton}
            onPress={() => router.push('/dashboard/gps/alert-rules')}
          >
            <Text style={styles.switchButtonText}>Gerir regras</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.filtersPanel}>
          <Text style={styles.filtersTitle}>Filtrar alertas</Text>

          <Text style={styles.groupLabel}>Tipo</Text>
          <View style={styles.filterGroup}>
            {[
              { key: 'all', label: 'Todos' },
              { key: 'speed', label: 'Velocidade' },
              { key: 'geofence', label: 'Geocerca' },
              { key: 'offline', label: 'Online/Offline' },
            ].map((item) => (
              <TouchableOpacity
                key={item.key}
                style={[styles.filterChip, typeFilter === item.key && styles.filterChipActive]}
                onPress={() => setTypeFilter(item.key as any)}
              >
                <Text style={[styles.filterChipText, typeFilter === item.key && styles.filterChipTextActive]}>{item.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={styles.groupLabel}>Ordenar por</Text>
          <View style={styles.filterGroup}>
            {[
              { key: 'newest', label: 'Mais recentes' },
              { key: 'oldest', label: 'Mais antigos' },
              { key: 'type', label: 'Tipo' },
            ].map((item) => (
              <TouchableOpacity
                key={item.key}
                style={[styles.filterChip, sortBy === item.key && styles.filterChipActive]}
                onPress={() => setSortBy(item.key as any)}
              >
                <Text style={[styles.filterChipText, sortBy === item.key && styles.filterChipTextActive]}>{item.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      {/* Alert List */}
      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.listContentContainer}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        {displayedAlerts.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="notifications-outline" size={64} color="#C7C7CC" />
            <Text style={styles.emptyStateTitle}>Nenhum alerta</Text>
            <Text style={styles.emptyStateText}>
              Não há alertas para exibir no momento
            </Text>
          </View>
        ) : (
          displayedAlerts.map((alert) => {
            const speedKmh = getSpeedKmh(alert);
            const primaryName = getPrimaryName(alert);
            const routeParams = buildRouteLinkParams(alert);
            const eventData = alert.event_data || {};
            const eventAttributes = eventData?.event?.attributes || eventData?.position?.attributes || {};
            return (
            <View
              key={alert.id}
              style={[
                styles.alertCard,
                alert.status === 'acknowledged' && styles.alertCardAcknowledged,
              ]}
            >
              <View style={styles.alertHeader}>
                <View style={styles.alertTitleRow}>
                  <Ionicons
                    name={getAlertIcon(alert.event_type)}
                    size={24}
                    color={getAlertColor(alert.event_type, alert.status)}
                  />
                  <View style={styles.alertInfo}>
                    <Text style={styles.alertDevice}>{primaryName}</Text>
                    <View style={styles.alertTypeRow}>
                      <Text style={styles.alertType}>{getFriendlyEventType(alert.event_type)}</Text>
                      {alert.event_type.toLowerCase().includes('speed') && speedKmh !== null && (
                        <View style={styles.speedBadge}>
                          <Text style={styles.speedBadgeText}>
                            {speedKmh.toFixed(1)} km/h
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>
                </View>
              </View>

              <Text style={styles.alertMessage}>{alert.message}</Text>

              {Object.keys(eventAttributes).length > 0 && (
                <View style={styles.attributesContainer}>
                  {Object.entries(eventAttributes).slice(0, 4).map(([key, value]) => {
                    const isSpeedAttr = key.toLowerCase().includes('speed');
                    const displayValue = isSpeedAttr && typeof value === 'number'
                      ? `${(value * 1.852).toFixed(1)} km/h`
                      : String(value);
                    return (
                      <View key={key} style={styles.attributeRow}>
                        <Text style={styles.attributeLabel}>{getFriendlyFieldLabel(key)}:</Text>
                        <Text style={styles.attributeValue}>{displayValue}</Text>
                      </View>
                    );
                  })}
                </View>
              )}

              <View style={styles.alertFooter}>
                <View style={styles.alertFooterItem}>
                  <Ionicons name="time-outline" size={14} color="#8E8E93" />
                  <Text style={styles.alertFooterText}>
                    Criado em {new Date(alert.created_at).toLocaleString('pt-PT')}
                  </Text>
                </View>

                {alert.acknowledged_at && (
                  <View style={styles.alertFooterItem}>
                    <Ionicons name="checkmark-done-outline" size={14} color="#34C759" />
                    <Text style={styles.alertFooterText}>
                      Reconhecido em {new Date(alert.acknowledged_at).toLocaleString('pt-PT')}
                    </Text>
                  </View>
                )}

                {routeParams && (
                  <TouchableOpacity
                    style={styles.routeButton}
                    onPress={() => router.push(routeParams as any)}
                  >
                    <Ionicons name="map-outline" size={16} color="#007AFF" />
                    <Text style={styles.routeButtonText}>Visualizar rota</Text>
                  </TouchableOpacity>
                )}
              </View>

              {alert.status === 'acknowledged' && alert.acknowledged_by_name && (
                <View style={styles.acknowledgedBanner}>
                  <Text style={styles.acknowledgedText}>
                    Reconhecido por {alert.acknowledged_by_name}
                  </Text>
                </View>
              )}
            </View>
          )})
        )}
      </ScrollView>
            </>
          )}
        </View>
      </Surface>

      {/* Mobile Sidebar */}
      {!isLargeScreen && (
        <Sidebar
          key={`mobile-sidebar-${width}`}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner && hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  container: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F2F2F7',
    zIndex: 1000,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F2F2F7',
  },
  header: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
    gap: 12,
  },
  menuButton: {
    padding: 4,
  },
  headerContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#000000',
  },
  refreshButton: {
    padding: 8,
  },
  filterContainer: {
    flexDirection: 'row',
    padding: 16,
    gap: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  filterButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    backgroundColor: '#F9F9F9',
    alignItems: 'center',
  },
  filterButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#8E8E93',
  },
  filterButtonTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  pageHeader: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
    gap: 12,
  },
  headerTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  pageTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#000000',
  },
  switcher: {
    flexDirection: 'row',
    backgroundColor: '#EEF2FF',
    borderRadius: 12,
    padding: 4,
    gap: 4,
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  switchButton: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
  },
  switchButtonActive: {
    backgroundColor: '#FFFFFF',
  },
  switchButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6B7280',
  },
  switchButtonTextActive: {
    color: '#111827',
  },
  filtersPanel: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    padding: 12,
    gap: 8,
  },
  filtersTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#334155',
    marginBottom: 2,
  },
  groupLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  filterGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 4,
  },
  filterChip: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
  },
  filterChipActive: {
    backgroundColor: '#EAF3FF',
    borderColor: '#007AFF',
  },
  filterChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4B5563',
  },
  filterChipTextActive: {
    color: '#007AFF',
  },
  list: {
    flex: 1,
  },
  listContentContainer: {
    flexGrow: 1,
  },
  alertCard: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 16,
    marginTop: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderLeftWidth: 4,
    borderLeftColor: '#007AFF',
  },
  alertCardAcknowledged: {
    borderLeftColor: '#8E8E93',
    opacity: 0.7,
  },
  alertHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  alertTitleRow: {
    flexDirection: 'row',
    gap: 12,
    flex: 1,
  },
  alertInfo: {
    flex: 1,
  },
  alertTypeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  alertDevice: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
  },
  alertType: {
    fontSize: 14,
    color: '#8E8E93',
  },
  speedBadge: {
    backgroundColor: '#FFE8E6',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  speedBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FF3B30',
  },
  alertMessage: {
    fontSize: 14,
    color: '#000000',
    marginBottom: 12,
    lineHeight: 20,
  },
  attributesContainer: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    padding: 10,
    marginBottom: 12,
    gap: 6,
  },
  attributeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  attributeLabel: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  attributeValue: {
    fontSize: 12,
    color: '#0F172A',
    flexShrink: 1,
    textAlign: 'right',
  },
  alertFooter: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  alertFooterItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  alertFooterText: {
    fontSize: 12,
    color: '#8E8E93',
  },
  routeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#EAF3FF',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  routeButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#007AFF',
  },
  acknowledgedBanner: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E5EA',
  },
  acknowledgedText: {
    fontSize: 12,
    color: '#34C759',
    fontStyle: 'italic',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 8,
    textAlign: 'center',
    paddingHorizontal: 20,
    maxWidth: 500,
  },
  infoBox: {
    width: '100%',
    backgroundColor: '#F9F9F9',
    padding: 16,
    borderRadius: 12,
    marginTop: 24,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 22,
    marginBottom: 12,
  },
  infoNote: {
    fontSize: 12,
    color: '#007AFF',
    fontStyle: 'italic',
  },
});







